/**
 * One cart write at a time, and never give up on a refusal.
 *
 * Shopify throttles writes to the cart. Two presses of the quantity stepper in quick succession
 * send two POSTs to /cart/change.js and the second comes back 429 with the cart untouched. The
 * theme's own code catches that, hides the spinner and stops — so the drawer never re-renders,
 * while the number in the box has already been stepped up by the browser. Shopify is on 2, the
 * customer sees 3, and the next press sends 4: the cart appears to jump from 2 to 4.
 *
 * Two rules fix it at the source, for every path that writes to the cart — the theme's, the
 * rewards bar's, and any app's:
 *
 *   Serialised. One write is in flight at a time; the next waits for it. Overlapping writes are
 *   what provokes the throttle, and they are also what makes two answers race to paint the drawer.
 *
 *   Retried. A 429 is a "not yet", not a failure. It is retried, backing off, and the caller is
 *   handed the answer as if it had worked first time — which it does.
 *
 * Reads (/cart.js, /cart?section_id=) are left alone: they are not throttled the same way and
 * queueing them would make the drawer feel slower for no reason.
 */
(function () {
  const WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  // How long to wait before trying again. Shopify's own advice for a throttled cart write is to
  // back off; these are deliberately short because a person is watching a spinner.
  const BACKOFF_MS = [400, 800, 1600];

  const nativeFetch = window.fetch;

  // The tail of the queue. Every write chains onto it, so they leave in the order they were made
  // and never overlap.
  let tail = Promise.resolve();

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function send(args, attempt) {
    const response = await nativeFetch.apply(window, args);
    if (response.status !== 429 || attempt >= BACKOFF_MS.length) return response;

    // Shopify sometimes says how long to wait. Trust it when it does.
    const stated = Number(response.headers.get('retry-after'));
    await sleep(Number.isFinite(stated) && stated > 0 ? stated * 1000 : BACKOFF_MS[attempt]);
    return send(args, attempt + 1);
  }

  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
    if (!url || !WRITE.test(url)) return nativeFetch.apply(this, args);

    // Chained onto the queue, and the queue is kept alive whatever happens to this one — a failed
    // write must not stop the writes behind it.
    const result = tail.then(() => send(args, 0));
    tail = result.catch(() => {});
    return result;
  };
})();
